// API layer for the Nurse Dashboard.
//
// Every call carries the explicit tenant headers (APP-MODE / ORG-CODE /
// USER-ACCESS-KEY / USERNAME) that the backend check_user_access() requires —
// the global axios interceptor does NOT add these, and omitting them returns a
// 400 (see the ADT bed-census lesson). Base URL comes from
// REACT_APP_BACKEND_ROUTE_URL, e.g. ".../app/api/".
import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL || "https://manage-dev.hlthtek.com/app/api/";

function authHeaders() {
  return {
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
  };
}

const qs = (params = {}) => {
  const clean = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  return clean.length ? `?${new URLSearchParams(clean).toString()}` : "";
};

async function get(path, params, config) {
  const res = await axios.get(`${BASE}${path}${qs(params)}`, { headers: authHeaders(), ...config });
  return res.data;
}
async function post(path, body) {
  const res = await axios.post(`${BASE}${path}`, body, { headers: authHeaders() });
  return res.data;
}
async function put(path, body) {
  const res = await axios.put(`${BASE}${path}`, body, { headers: authHeaders() });
  return res.data;
}
async function patch(path, body) {
  const res = await axios.patch(`${BASE}${path}`, body, { headers: authHeaders() });
  return res.data;
}
async function del(path, body) {
  const res = await axios.delete(`${BASE}${path}`, { headers: authHeaders(), data: body });
  return res.data;
}

// Pull a list out of the various response envelopes the backend uses.
export const listOf = (payload) =>
  payload?.data?.data ||
  payload?.data ||
  payload?.results ||
  (Array.isArray(payload) ? payload : []);

// --- Admissions / census -------------------------------------------------
export const getAdmissions = (params) => get("ipd_admission/", params);

// --- Dashboard summary ---------------------------------------------------
export const getSummary = (params) => get("nurse_dashboard/summary/", params);

// --- Vitals --------------------------------------------------------------
export const getVitals = (admission_id, params) =>
  get("nurse_dashboard/vitals/", { admission_id, ...params });
// One latest vital per admission, in a single request (overview grid).
export const getVitalsLatest = (admissionIds, params) =>
  get("nurse_dashboard/vitals/", { admission_ids: admissionIds.join(","), latest_per_admission: 1, ...params });
export const addVitals = (payload) => post("nurse_dashboard/vitals/", payload);

// --- Risk assessment -----------------------------------------------------
export const getAssessment = (admission_id, params) =>
  get("nurse_dashboard/assessment/", { admission_id, ...params });
export const addAssessment = (payload) => post("nurse_dashboard/assessment/", payload);

// --- Intake / Output -----------------------------------------------------
export const getIntakeOutput = (admission_id, params) =>
  get("nurse_dashboard/intake_output/", { admission_id, ...params });
export const addIntakeOutput = (payload) => post("nurse_dashboard/intake_output/", payload);

// --- eMAR ------------------------------------------------------------------
// nurse_dashboard/med_admin/ removed 2026-07-27 (backend + these wrappers):
// eMAR now projects off CarePlanTasks via fulfillCarePlanTask() — see
// EmarOverview.js / MyTasks.js. Confirmed unused before removal.

// --- Nursing notes -------------------------------------------------------
export const getNotes = (admission_id, params) =>
  get("nurse_dashboard/nursing_notes/", { admission_id, ...params });
export const addNote = (payload) => post("nurse_dashboard/nursing_notes/", payload);

// --- Manager: roster / assignments / transfers ---------------------------
export const getRoster = (params) => get("nurse_dashboard/nurse_roster/", params);
export const getAssignments = (params) =>
  get("nurse_dashboard/nurse_bed_assignment/", params);
// The logged-in nurse's own IN-FORCE assignments. mine=1 makes the backend
// scope to the JWT identity (current_actor) AND to the shift in progress — the
// FE has no reliable nurse_id in sessionStorage (doctor_id is blank for nurse
// logins), so we must NOT pass one, and we must NOT pass a date either.
// Used by My Patients to show only patients in this nurse's assigned beds.
export const getMyAssignments = () =>
  get("nurse_dashboard/nurse_bed_assignment/", { mine: 1 });
// payload: { nurse_id, bed_code, valid_from, valid_to, shift } — all required.
// valid_from/valid_to are inclusive YYYY-MM-DD dates and shift is a lowercase
// code from the facility's shift schedule for valid_from (no longer a fixed
// morning/evening/night enum). A pin is in force only for that shift on
// those dates; before 2026-08-14 a pin had no window and never expired.
// The backend validates each and responds 200 {status:"Failed"} on a bad
// value — check res.status, don't assume a 2xx means success. Dedupe is
// per (nurse, bed, shift) on OVERLAPPING windows; a repeat assign returns
// Success with empty data.
export const assignBed = (payload) =>
  post("nurse_dashboard/nurse_bed_assignment/", payload);
// Bulk assign — payload: { assignments: [{ nurse_id, bed_code, valid_from,
// valid_to, shift }, ...] }. Used by the ward-assignment mode to assign every
// selected nurse to every bed in a ward in one request. Returns {status, data
// (created rows), skipped (overlap dedupe no-ops), errors}, so callers MUST
// read skipped/errors, not just status, to report the real count.
export const bulkAssignBeds = (assignments) =>
  post("nurse_dashboard/nurse_bed_assignment/", { assignments });
// payload: prefer { id } (the assignment row's PK) — precise, deactivates
// exactly that one shift-row — or { ids: [...] } for several in one request.
// The compound { nurse_id, bed_code } form is back-compat only and now
// deactivates ALL shift-rows for that pair.
export const unassignBed = (payload) =>
  del("nurse_dashboard/nurse_bed_assignment/", payload);
export const getTransferLog = (params) => get("nurse_dashboard/transfer_log/", params);

// --- Beds ----------------------------------------------------------------
export const getBeds = (params) => get("ipd_bed/", params);

// --- Care-plan tasks (reused existing endpoint) --------------------------
export const getCarePlanTasks = (params) => get("care_plan_tasks/", params);

// --- Task fulfillment (nurse records the dose/administration) -------------
// Unlike care_plan_tasks/, this endpoint enriches each task with what was
// ordered (order_details) AND the fulfillment_form the nurse fills to record
// it — so it is the one to drive the "record a dose" UI. Flat envelope
// {status, count, data, is_valid}; each row carries fulfillment_form =
// service_fulfillment_type.structure (i.e. read `.fulfillment_form` off it).
// params: admission_id?, date? (YYYY-MM-DD, matches scheduled_datetime date),
// status? (pending|in_progress|completed|missed), performed_by_id?.
export const getNurseTasks = (params) => get("nurse_dashboard_tasks/", params);
// The library of fulfillment form definitions. Optional filters: is_active,
// care_plan_id. Rarely needed directly — getNurseTasks already inlines the
// form per task — but useful to preview a care plan's form before ordering.
export const getServiceFulfillmentTypes = (params) =>
  get("service_fulfillment_types/", params);
// care_plan_data_summary/ wrapper removed with the MAR change: the Tasks tab was
// its only caller, and its rollup spans every day while the tab now shows one —
// the tally and the list would have disagreed. Counts are derived from the rows
// on screen instead. The endpoint still exists server-side.

// --- Care plans (E9 D9 — Doctor Instructions) -----------------------------
// GET /care_plans/ -> ManageCarePlanView.get(). Identity-gated so a nurse JWT
// can read it (E9-BE), unlike the ungated POST /care_plan_data/. Flat envelope
// ({status, count, data, is_valid}) — callers MUST check res.status ===
// "Success". Each row carries template_id; filter to 8 for clinical_notes.
export const getCarePlans = (admission_id) => get("care_plans/", { admission_id });
// Persists a task tick-off. Envelope is flat (no `data` wrapper): the
// caller must check the resolved payload's own `status`/`message`, not a
// nested `.data.status` — see fulfill_care_plan_task.py's Response bodies.
export const fulfillCarePlanTask = (payload) => put("fulfill_care_plan_task/", payload);

// --- Ad-hoc dose: as-needed (SOS/PRN), STAT, or an extra dose --------------
// An as-needed order schedules NO doses (a PRN dose has no appointed time), so
// there is no task to fulfill — this creates and completes one in a single
// call. The recording nurse is stamped server-side from the JWT; do NOT send an
// identity. Flat envelope, and a validation failure returns is_valid:true so a
// rejected dose can never trip the multi-machine-logout guard mid-round.
// GET first to learn which fulfillment form applies (the client must not
// re-implement that rule): -> { fulfillment_type_id, fulfillment_form }.
export const getAdHocDoseForm = (prescriptionId) =>
  get("administer_medication_now/", { prescription_id: prescriptionId });
// payload: { prescription_id, data: { ...the filled form... } }
export const administerMedicationNow = (payload) =>
  post("administer_medication_now/", payload);

// --- Typeahead masters (Place Order UX) ------------------------------------
// SNOMED-backed trigram search (main/search/medicinesearch.py). JWT-gated.
// GET search_medicines/?name=<term> -> {status, data:[{term, conceptId,...}]}
export const searchMedicines = (name) => get("search_medicines/", { name });
// Same contract for lab/test panels (accepts name or term).
export const searchTestPanels = (name) => get("search_test_panels/", { name });
// Facility's active doctors -> {data:[{doctor_id, name, username, ...}]}.
// Drives the ordering-doctor picker: display the NAME, submit the doctor_id
// (the id is an opaque hex string — never show it as the primary label).
export const getAllDoctors = () => get("get_all_doctors/");

// --- Medication order book (CPOE pipeline) --------------------------------
// The admission's prescription rows — ONE ROW PER MEDICINE, rows of the same
// order share order_no. Row.status is the medicine's lifecycle position
// (Sent -> Acknowledged -> Dispensed -> Received; Cancel), so a part-filled
// order shows mixed statuses. POST body {admission_id}.
export const getPrescriptionsByAdmission = (admissionId) =>
  post("get_ipd_prescriptions_by_admission/", { admission_id: admissionId });
// Order/row-scoped status transition. payload: {admission_id, status,
// order_no? | prescription_ids?, reason?}. Forward-only; 'Received' is stamped
// with the JWT actor server-side (send NO identity fields); check the
// envelope's updated_ids/rejected — a 200 "Success" can still reject rows.
export const updatePrescriptionStatus = (payload) =>
  post("update_ipd_prescription_status/", payload);
// Ward stock per dispensed medicine, DERIVED (never stored): total_quantity −
// ward-stock doses administered − returned. Only rows that reached
// Dispensed/Received appear, so an absent row means "not dispensed yet" —
// distinct from `returnable: null`, which means the order had no computable
// quantity. returned_units is still 0 pending the pharmacy→order link.
// -> {prescription_id, order_no, medicine_name, status, cancelled,
//     total_quantity, administered_units, returned_units, returnable}.
export const getMedicationReturnable = (admissionId) =>
  get("medication_returnable/", { admission_id: admissionId });

// --- Medication order creation (pharmacy contract) ------------------------
// POST add_ipd_prescription/ -> writes the order book (main_ipd_prescription,
// one row per medicine sharing an order_no) AND schedules the dose/eMAR tasks
// server-side. Resolves the admission from patient_id (NOT admission_id).
// payload: { patient_id, medicines: [{ medicine_name, generic_medicine_names?,
// details: { dosage, frequency, duration (number), duration_unit, route } }],
// ipd_flag: true, status: "Sent", ordering_doctor_id?, provisional_diagnosis?,
// allergies?, final_diagnosis? }. Flat envelope — check res.status.
export const addIpdPrescription = (payload) => post("add_ipd_prescription/", payload);

// --- Pharmacy dispensing receipt (external pharmacy service, proxied) -------
// GET the delivered-but-unconfirmed queue for the whole facility (no filter):
// each row is self-contained {transaction_id, transaction_number, patient_name,
// items:[{line_item_id, item_name, batch_number, requested_quantity, unit_rate}]}.
export const getPendingConfirmations = () =>
  get("inventory/pharmacy/dispensing/pending-confirmation/");
// POST the nurse's accept/reject decision for every line of one transaction.
// A decision is mandatory for EVERY line (partial omission is a 422). confirmed_by
// is stamped server-side from the JWT — do NOT send it. On success the pharmacy
// service consumes stock and the row leaves the queue. It does NOT bill the
// patient: charge capture is a separate call the caller must make (see
// postPharmacyChargeCapture below).
// payload: { remarks?, line_items: [{ line_item_id, accepted_quantity,
// rejected_quantity, rejection_reason? }] }.
export const confirmDispense = (transactionId, payload) =>
  post(`inventory/pharmacy/dispensing/${transactionId}/confirm/`, payload);
// The delivered lines of one dispense as the pharmacy service holds them. An
// IPD line carries item_payload {prescription_id, order_no}: the order-book row
// it fills, written when the pharmacy created the dispense. The pending queue
// above drops that field, so receipt reads it from here.
export const getDispenseItems = (transactionId) =>
  get(`inventory/pharmacy/dispensing/${transactionId}/items/`);
// Bills the patient for a confirmed delivery. Despite what the comment above
// once claimed, confirming does NOT capture the charge — nothing did, so
// received stock was never billed. The caller posts this after a successful
// confirm, using the ACCEPTED quantities (see pharmacyCharges.js).
// payload: { items: [{ item_id, item_name, batches: [{ quantity,
// batch_number, unit_rate, expiry_date? }] }] }. One batch → one charge line.
export const postPharmacyChargeCapture = (admissionId, payload) =>
  post(`ipd_admission/${admissionId}/billing/pharmacy-charge-capture/`, payload);
// Closes/completes IPD prescriptions once received on the ward.
export const completePrescriptionReceipt = (admissionId, payload) =>
  post("update_ipd_prescription_status/", {
    admission_id: admissionId,
    status: "Received",
    ...payload,
  });

// --- Patient ward return (unused meds back to pharmacy) -------------------
// What a patient can return: one row per item+batch still held on the ward,
// names pre-resolved. ward_held = accepted − returned (already excludes prior
// returns). Does NOT yet subtract administered doses (see ward-return-flow-SPEC
// §1a) — the nurse enters the physical leftover count, capped at ward_held.
// { item_id, uom_id, item_name, batch_number, unit_rate, ward_held,
//   accepted_quantity, returned_quantity, source_transaction_id,
//   source_transaction_number, from_store_id, store_name }.
export const getReturnableItems = (admissionId, patientId) =>
  get("inventory/pharmacy/dispensing/returnable/", { admission_id: admissionId, patient_id: patientId });
// Create a patient ward return. payload: { patient_id, admission_id,
// return_type:"WARD_RETURN", source_reference_id, to_store_id,
// items:[{ item_id, uom_id, batch_number, requested_quantity, unit_rate, remarks }] }.
// batch_number comes from the returnable row the quantity was taken off — the
// nurse never picks it. source_reference_id is that row's source_transaction_id
// (the dispense UUID), NOT the ORD-… number. The pharmacy assigns the RET-… number.
export const createWardReturn = (payload) =>
  post("inventory/pharmacy/ward-returns/", payload);

// --- Discharge (reused existing endpoint — same one NurseTasks.js's
// Discharge Advice toggle calls) ------------------------------------------
export const initiateDischarge = (admissionId, dischargeInitiated) =>
  post("discharge_initiation/", { admission_id: admissionId, discharge_initiated: dischargeInitiated });

// --- Nurse-placed orders (nursing-ops sprint, Epic 10 / D4) --------------
// Medication (template_id 3) and Test (template_id 6) care plans, filed by
// the nurse through the existing submit_care_plan/ pipeline. The backend
// derives actor_role from the JWT (P2) and checks allowed_actors from the
// tenant DB (P2b); allowed_actors for template_id 3/6 was seeded to include
// 'Nurse' (E10-BE). See main/ipd/submit_care_plan.py and
// main/ipd/manage_care_plans_with_fulfillment.py.
//
// GET /care_plans_with_fulfillment/?actor=<role> -> flat envelope
// {status, count, data:[{id, name, template_id, structure, multi_entry,
// allowed_actors, default_fulfillment_types}], is_valid}. actor filters to
// only the care plans that role may initiate.
export const getCarePlansWithFulfillment = (actor) =>
  get("care_plans_with_fulfillment/", { actor });
// POST /submit_care_plan/ -> {status, message, care_plan_data_ids, count,
// skipped, is_valid}. Do NOT send actor_role/initiated_by — the backend
// derives identity from the JWT and ignores any caller-supplied values (P2);
// sending them is harmless but pointless. IMPORTANT: a "Success" envelope can
// still carry rejected entries in `skipped` (e.g. actor not permitted for
// that care plan) — callers MUST check `skipped`/`care_plan_data_ids`, not
// just `status`, or a rejected order reads as a false "placed" success.
export const submitCarePlan = (admissionId, carePlans) =>
  post("submit_care_plan/", { admission_id: admissionId, care_plans: carePlans });

// --- Nurse ward roster (nursing-ops sprint, Epic 7) -----------------------
// Grain: one row per (nurse, ward, shift, date), where a "ward" IS ward_code —
// a RoomCategory.code (ICU/General/Private), NOT the department-level
// main_ipd_ward table. It used to be paired with a department, on the theory
// that Cardiology's ICU differed from Orthopaedics'; a shared ICU is one ward
// staffed by one team. A layer above
// NurseBedAssignment, not a replacement for it. shift is lowercase
// a code from the facility's shift schedule for roster_date; status is
// "on_duty"|"overtime"|"leave"|"na" (VALID_STATUSES). See
// main/ipd/manage_nurse_ward_roster.py.
// params: ward_code?, nurse_id?, shift?, from_date?, to_date?, is_active?
export const getWardRoster = (params) => get("nurse_dashboard/nurse_ward_roster/", params);
// payload: { nurse_id, nurse_name?, ward_code, shift,
// roster_date, end_date?, status, created_by? }. ward_code (RoomCategory.code)
// is required — the backend validates it and rejects with "Ward not found".
// end_date (inclusive) expands the write into one row
// per calendar date server-side. status is "Failed" only when NOTHING was
// created (every date rejected); a partial or full success is "Success".
// Either way the body carries the per-date outcomes — data (created rows),
// skipped (dedupe no-ops), errors (rejections, e.g. the overtime-cap or a
// unique-constraint clash) — so callers must read skipped/errors regardless
// of status, not rely on the top-level status alone.
export const addWardRoster = (payload) => post("nurse_dashboard/nurse_ward_roster/", payload);
// Only `status` is mutable; the backend re-checks the overtime cap and
// returns a 200-Failed envelope if the new status would breach it.
export const updateWardRosterStatus = (id, status) =>
  put(`nurse_dashboard/nurse_ward_roster/${id}/`, { status });
export const removeWardRoster = (id) => del(`nurse_dashboard/nurse_ward_roster/${id}/`);

// --- Nursing Ops 2 (spec: nursing-ops-2-SPEC.md) --------------------------
// My Schedule (#01): the logged-in nurse's own roster rows. mine=1 resolves
// the nurse SERVER-side from the JWT (current_actor) — there is no usable
// nurse_id in sessionStorage, so never try to filter client-side.
export const getMyRoster = (params) =>
  get("nurse_dashboard/nurse_ward_roster/", { mine: 1, ...params });
// Rostered hours (#04): per-nurse hours over a window (defaults to the
// current Mon-Sun week BE-side) — the same rows the overtime cap counts.
export const getRosterHours = (params) => get("nurse_dashboard/nurse_ward_roster/hours/", params);
// Copy week forward (#06): copies the week starting source_from (+7 days),
// same per-row validation as authoring; response carries data/skipped/errors.
export const copyRosterWeek = (sourceFrom) =>
  post("nurse_dashboard/nurse_ward_roster/copy_week/", { source_from: sourceFrom });
// Leave requests (#02/#03). Lifecycle: pending -> approved/rejected (manager),
// pending -> withdrawn (owner only), approved -> revoked (manager). Applying
// never sends a nurse id — the backend records the JWT's nurse. Approval
// flips overlapping roster rows to `leave` and hard-blocks the range.
export const getLeaveRequests = (params) => get("nurse_dashboard/leave_request/", params);
export const getMyLeaveRequests = () => get("nurse_dashboard/leave_request/", { mine: 1 });
export const applyLeave = ({ from_date, to_date, reason }) =>
  post("nurse_dashboard/leave_request/", { from_date, to_date, reason });
export const actOnLeave = (id, action, note) =>
  put(`nurse_dashboard/leave_request/${id}/`, { action, note });

// --- Shift handover (nursing-ops sprint, Epic 9 / D11) --------------------
// State machine: draft -> submitted -> acknowledged. See
// main/ipd/manage_shift_handover.py. Flat envelope {status, count/id, data,
// is_valid} throughout — callers MUST check res.status === "Success".
//
// GET: pass { id } for a single handover, or filter with any of ward_id,
// department_id, shift_date, outgoing_shift, status, handed_over_by. Each
// handover already carries `patients: [...]` (the auto-populated per-patient
// SBAR rows, see _serialize_handover) — no separate fetch needed to see them.
export const getShiftHandovers = (params) => get("nurse_dashboard/shift_handover/", params);
// payload: { handed_over_by, shift_date (YYYY-MM-DD), outgoing_shift,
// incoming_shift (lowercase morning|evening|night, VALID_SHIFTS), ward_id or
// department_id (one required), general_notes? }. Backend auto-creates one
// ShiftHandoverPatient (SBAR) row per admitted patient on that ward/department.
// Response is {status, message, id, patient_count, is_valid} — no `data` —
// refetch (getShiftHandovers) to see the populated patients.
export const createShiftHandover = (payload) => post("nurse_dashboard/shift_handover/", payload);
// payload: { id, action: "submit"|"acknowledge", actor, general_notes? (submit
// only) }. submit requires status===draft; acknowledge requires
// status===submitted; both require actor. The backend rejects an out-of-state
// transition with a 200 {status:"Failed"} envelope — surface res.message.
export const setShiftHandoverStatus = (payload) => put("nurse_dashboard/shift_handover/", payload);
// payload: { patients: [{ id, situation?, background?, assessment?,
// recommendation?, special_instructions?, priority? }, ...] }. A row is only
// editable while its parent handover is status===draft — otherwise it lands
// in the response's `skipped` array even though the top-level status is
// "Success", so callers must check `skipped`, not just `status`.
export const updateShiftHandoverPatients = (payload) => put("nurse_dashboard/shift_handover_patient/", payload);

// --- Facility shift schedule (2026-08-17) ---------------------------------
// The shift clock is tenant configuration, effective-dated: a version is a
// complete 24-hour schedule that applies from a date onward. GET with no
// params lists versions (newest first) plus `today` — the backend's IST date,
// which is what to trust when deciding what counts as "future", not the
// browser clock.
export const getShiftSchedules = () => get("nurse_dashboard/shift_schedule/");
// The shift set in force on one date -> { effective_on, shifts: [...] }.
// Resolved through the same helper the coverage gate uses, so this can never
// disagree with what the backend will actually enforce.
// Callers asking for the same date while a request is in flight share it: a
// screen mounts several components that each want the schedule at once, and
// every request counts against the server's per-second limit.
const scheduleAtInFlight = new Map();
export const getShiftScheduleAt = (date) => {
  if (!scheduleAtInFlight.has(date)) {
    scheduleAtInFlight.set(
      date,
      get("nurse_dashboard/shift_schedule/", { at: date }).finally(() => scheduleAtInFlight.delete(date))
    );
  }
  return scheduleAtInFlight.get(date);
};
// payload: { effective_from (YYYY-MM-DD, strictly future), note?, shifts: [
// { code, label, start_time "HH:MM", end_time "HH:MM", sort_order }] }.
// A "Failed" envelope may carry `conflicts` — existing roster rows/pins using a
// shift this schedule drops. Callers MUST render those, not just the message.
export const publishShiftSchedule = (payload) => post("nurse_dashboard/shift_schedule/", payload);
// Cancels a version that has not taken effect yet. One already in force is
// refused — supersede it with a later version instead.
export const cancelShiftSchedule = (id) => del(`nurse_dashboard/shift_schedule/${id}/`);

// --- Theatre calls -------------------------------------------------------
//
// Theatre asking this ward for a patient. Served by the OT module but auth'd as
// ward work, not OT work: a nurse answering a call slip should not need an OT
// permission to say the patient has left.
//
// `patient_sent` is the ward check-out, bed decision included — not a status
// tick. It is why this is one button and not two.
export const getTheatreCalls = (params, config) => get("ot/theatre_calls/", params, config);
export const acknowledgeTheatreCall = (slipId, body) =>
  patch(`ot/theatre_calls/${slipId}/`, body || {});
export const sendPatientToTheatre = (slipId, body) =>
  post(`ot/theatre_calls/${slipId}/patient_sent/`, body || {});
// The backend's message, word for word, for a hospital with no OT at all
// (main/ot/ot_common.py OT_NOT_SET_UP). The shell matches it to hide Theatre
// Calls, so the two must stay in step.
export const OT_NOT_SET_UP = "Theatre is not set up for this hospital";

// --- Ward Incharge & Ward Scoping (three-tier nursing hierarchy) ---------
export const getMyAssignedWards = () => get("nurse_dashboard/my_assigned_wards/");
export const getInchargeAssignments = (params) => get("nurse_dashboard/incharge_assignments/", params);
export const assignWardIncharge = (payload) => post("nurse_dashboard/incharge_assignments/", payload);
export const removeWardIncharge = (id) => del(`nurse_dashboard/incharge_assignments/${id}/`);

